import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Grid from '@material-ui/core/Grid'
import CustomButton from '../custom-material/button/Button'
import CustomTypography from '../custom-material/typography/Typography'
import DatabasePreferencesExpansionPanel from './DatabasePreferencesExpansionPanel'
import EmailPreferenceExpansionPanel from './EmailPreferenceExpansionPanel'
import PublicationDrugTopicsCard from './PublicationDrugTopicsCard'
import PublicationSettingsExpansionPanel from './PublicationSettingsExpansionPanel'
import WatchListAlertExpansionPanel from './WatchListAlertExpansionPanel'
import GeneralAlertsExpansionPanel from './GeneralAlertsExpansionPanel'
import ProductAlertExpansionPanel from './ProductAlertExpansionPanel'
import PromptFilterPreferredDialog from './PromptFilterPreferredDialog'
import { getBlob } from '../../libs/api'
import { saveAs } from 'file-saver'

import { connect } from 'react-redux'


const styles = theme => ({
  card: {
    margin: 16
  },
  cardHeader: {
    backgroundColor: '#2961B2',
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 16,
    paddingRight: 16
  },
  cardTitleText: {
    color: 'white',
    fontWeight: 'normal'
  },
  cardContent: {
    padding: '16px !important'
  },
  downloadGuideButton: {
    backgroundColor: '#E1EDF8',
    color: '#1A2F5A',
    fontWeight: 'bold',
    width: 275,
    '&:hover': {
      backgroundColor: '#CAD5DF'
    }
  },
  fullGuide: {
    color: '#0000FF',
    textDecoration: 'underline',
    cursor: 'pointer'
  },
  flexRowSpaceBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  helpIcon: {
    marginLeft: 4,
    fontSize: 22,
    color: '#00ACEF'
  }
})

const TitleCard = ({classes, otherEmailAddress, downloadBrochure}) => (
  <Card className={classes.card}>
    <CardHeader
      className={classes.cardHeader}
      title={
        <div className={classes.flexRowSpaceBetween}>
          <CustomTypography className={classes.cardTitleText} variant='h6'>
            {otherEmailAddress && <div>{otherEmailAddress}</div>}
            Customize Emails &amp; Database Alerts
          </CustomTypography>
          <CustomButton className={classes.downloadGuideButton} variant='contained' size='small'  onClick={downloadBrochure}>
            Download Guide &amp; Topic Definitions
          </CustomButton>
        </div>
      }
    />
    <CardContent className={classes.cardContent}>
      <CustomTypography variant='body1'>Customize your emails to receive publications on topics that you are interested in. </CustomTypography>
      <CustomTypography variant='body1'>Not sure where to start? Download the <span className={classes.fullGuide} onClick={downloadBrochure}>full guide</span> to find out more.</CustomTypography>
    </CardContent>
  </Card>
)

class ManageEmailsComponent extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {promptFilterPreferredDialogOpen: false}

    this.closePromptFilterPreferred = () => this.setState({promptFilterPreferredDialogOpen: false})

    this.saveEmailPreferences = (emailPreferences, categories, savedPreferenceName) => {
      // prompt to turn on/off filter_preferred on last preference removed or first added
      const oldCount = this.props.emailPreferences.originalPreferences.filter(x => x.name).length
      const newCount = emailPreferences.filter(x => x.name).length
      const isFiltered = this.props.exclusions.filter_preferred === true && !this.props.isAllPublicationsSuspended

      this.props.saveEmailPreferences(emailPreferences, categories, savedPreferenceName)

      if (isFiltered && newCount === 0 && oldCount > 0) {
        // removed last one
        this.setState({promptFilterPreferredDialogOpen: true})
      } else if (!isFiltered && oldCount === 0 && newCount === 1) {
        // added first one
        this.setState({promptFilterPreferredDialogOpen: true})
      }
    }

    this.downloadBrochure = async () => {
      const brochurePath = 'Email Preferences/IPDAnalytics_EmailPreferencesGuide.pdf'
      const { origin } = this.props
      const eBrochurePath = brochurePath.split('/').map(x => encodeURIComponent(x)).join('/')
      try {
        const fileBlob = await getBlob(`${origin || ''}/learn-more-images/${eBrochurePath}`)
        saveAs(fileBlob, brochurePath.split('/').pop())
      } catch (error) {
        console.error(error)
      }
    }
  }

  render () {
    const {
      classes,
      history,
      contact,
      exclusions,
      emailPreferences,
      updateEmailPreferences,
      toggleExclusion,
      toggleGeneralExclusion,
      toggleFilterPreferred,
      enableSuspendAll,
      disableSuspendAll,
      isAllWatchlistsSuspended,
      isAllGeneralAlertsSuspended,
      isAllPublicationsSuspended,
      databasePreferences,
      updateDatabasePreferences,
      hasFavorites,
      managingOther,
      loadingFilterResults,
      fetchFilterResults,
      dismissFirstTimeHelp,
      isAdmin,
      claims
    } = this.props
    const {promptFilterPreferredDialogOpen} = this.state

    const showFirstTimeHelp = contact && !managingOther && databasePreferences && databasePreferences.interstitial.shouldShow

    return (
      <div className={classes.content}>
        <TitleCard classes={classes} otherEmailAddress={managingOther ? contact.emailAddress : ''} downloadBrochure={this.downloadBrochure} />
        <DatabasePreferencesExpansionPanel
          history={history}
          databasePreferences={databasePreferences}
          updateDatabasePreferences={updateDatabasePreferences}
          claims={claims}
        />
        <WatchListAlertExpansionPanel
          contact={contact}
          exclusions={exclusions}
          toggleExclusion={toggleExclusion}
          enableSuspendAll={enableSuspendAll}
          disableSuspendAll={disableSuspendAll}
          isAllWatchlistsSuspended={isAllWatchlistsSuspended}
        />
        <ProductAlertExpansionPanel
          exclusions={exclusions}
          toggleExclusion={toggleExclusion}
        />
        <div style={{ display: isAdmin ? 'block' : 'none', paddingBottom: 16 }}>
          <GeneralAlertsExpansionPanel
            contact={contact}
            exclusions={exclusions}
            toggleExclusion={toggleGeneralExclusion}
            enableSuspendAll={enableSuspendAll}
            disableSuspendAll={disableSuspendAll}
            isAllGeneralAlertsSuspended={isAllGeneralAlertsSuspended}
          />
        </div>
        <Grid container spacing={0}>
          <Grid item xs={8}>
            <Grid container direction='row' spacing={2}>
              <Grid item xs={12}>
                <PublicationSettingsExpansionPanel
                  contact={contact}
                  exclusions={exclusions}
                  toggleFilterPreferred={toggleFilterPreferred}
                  enableSuspendAll={enableSuspendAll}
                  disableSuspendAll={disableSuspendAll}
                  isAllPublicationsSuspended={isAllPublicationsSuspended}
                  showFirstTimeHelp={!!showFirstTimeHelp}
                  dismissFirstTimeHelp={dismissFirstTimeHelp}
                />
              </Grid>
              <Grid item xs={12}>
                <EmailPreferenceExpansionPanel
                  emailPreferences={emailPreferences}
                  updateEmailPreferences={updateEmailPreferences}
                  saveEmailPreferences={this.saveEmailPreferences}
                />
                {promptFilterPreferredDialogOpen
                  ? <PromptFilterPreferredDialog
                    dialogOpen={promptFilterPreferredDialogOpen}
                    handleCloseDialog={this.closePromptFilterPreferred}
                    confirm={toggleFilterPreferred}
                    turnOn={exclusions.filter_preferred !== true}
                  />
                  : null
                }
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4}>
            <Grid container direction='row' spacing={0}>
              <Grid item xs={12}>
                <PublicationDrugTopicsCard
                  emailPreferences={emailPreferences} managingOther={managingOther} hasFavorites={hasFavorites} loadingFilterResults={loadingFilterResults} fetchFilterResults={fetchFilterResults}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    )
  }
}

ManageEmailsComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object,
  contact: PropTypes.object,
  exclusions: PropTypes.object,
  emailPreferences: PropTypes.object,
  updateEmailPreferences: PropTypes.func,
  saveEmailPreferences: PropTypes.func,
  toggleExclusion: PropTypes.func.isRequired,
  toggleGeneralExclusion: PropTypes.func.isRequired,
  toggleFilterPreferred: PropTypes.func.isRequired,
  enableSuspendAll: PropTypes.func.isRequired,
  disableSuspendAll: PropTypes.func.isRequired,
  isAllWatchlistsSuspended: PropTypes.bool,
  isAllGeneralAlertsSuspended: PropTypes.bool,
  isAllPublicationsSuspended: PropTypes.bool,
  databasePreferences: PropTypes.object,
  updateDatabasePreferences: PropTypes.func.isRequired,
  hasFavorites: PropTypes.bool.isRequired,
  managingOther: PropTypes.bool.isRequired,
  loadingFilterResults: PropTypes.arrayOf(PropTypes.number).isRequired,
  fetchFilterResults: PropTypes.func.isRequired,
  dismissFirstTimeHelp: PropTypes.func.isRequired,
  claims: PropTypes.arrayOf(PropTypes.string).isRequired
}

 const mapStateToProps = (state) => ({
   // adjust the paths if your state shape differs
   isAdmin: state.user && state.user.isAdmin,
   userName: state.user && (state.user.userName || state.user.username || state.user.name || state.user.email),
   claims: state.user && state.user.claims,
 });

 export default connect(mapStateToProps)(
   withStyles(styles)(ManageEmailsComponent)
 )
